import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Field, controlBaseClasses } from './Field';
import { Icon, type IconName } from './Icon';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string | null;
  iconStart?: IconName;
  /** Rendered inside the field on the trailing edge — e.g. a unit or shortcut. */
  suffix?: React.ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hideLabel,
    hint,
    error,
    iconStart,
    suffix,
    required,
    className,
    containerClassName,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      {({ controlId, describedBy, invalid }) => (
        <div className="relative flex items-center">
          {iconStart ? (
            <Icon
              name={iconStart}
              size={15}
              className="pointer-events-none absolute left-2.5 text-content-muted"
            />
          ) : null}
          <input
            ref={ref}
            id={controlId}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            required={required}
            className={cn(
              controlBaseClasses,
              'h-9 px-3',
              iconStart && 'pl-8',
              suffix && 'pr-16',
              focusRing,
              className,
            )}
            {...rest}
          />
          {suffix ? (
            <span className="pointer-events-none absolute right-3 text-dense text-content-muted">
              {suffix}
            </span>
          ) : null}
        </div>
      )}
    </Field>
  );
});
